import React from 'react';
import { FaCar, FaCheckCircle, FaPhoneSquare, FaUser } from 'react-icons/fa';

import axios from 'helpers/axios';
import config from 'helpers/config';
import RepairOrder from 'models/RepairOrderModel';
import Service from 'models/ServiceModel';
import ServiceAuthorizationStatus from 'models/types/ServiceAuthorizationStatus';
import Toggle from 'components/common/Toggle';
import Price from 'components/common/Price';
import SignatureAuthorizationModal from './SignatureAuthorizationModal';
import TenantSettings from 'models/TenantSettingsModel';

interface Props {}

interface State {
    repairOrderId: string;
    repairOrder?: RepairOrder;
    services: Service[];

    // Ids of the still-pending services the customer has toggled on. Only
    // pending services can be in here - anything already authorized/declined is
    // locked and not part of the selection.
    approvedServiceIds: string[];

    isLoading: boolean;
    isAuthorizationModalOpen: boolean;
    // Set once the customer submits successfully in this session, so the page
    // can confirm what happened instead of silently snapping back.
    hasJustAuthorized: boolean;
    tenantSettings?: TenantSettings;
}

const isDecided = (service: Service) => service.authorizationStatus !== ServiceAuthorizationStatus.NotAuthorized;

const sortItemsByCreatedAt = (service: Service) => {
    service.items?.sort((a, b) => {
        const c = new Date(a.createdAt);
        const d: any = new Date(b.createdAt);
        return c > d ? 1 : -1;
    });
    return service;
};

class EstimatePage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        // @ts-ignore
        const { repairOrderId } = this.props.match.params;

        this.state = {
            repairOrderId,
            repairOrder: undefined,
            isLoading: true,
            services: [],
            approvedServiceIds: [],
            isAuthorizationModalOpen: false,
            hasJustAuthorized: false,
            tenantSettings: undefined,
        };

        this.onAuthorized = this.onAuthorized.bind(this);
    }

    componentDidMount() {
        const RO_Request = axios.get(`${config.apiUrl}/repairOrder/public/${this.state.repairOrderId}`);
        const servicesRequest = axios.get(`${config.apiUrl}/repairOrder/public/${this.state.repairOrderId}/services`);
        Promise.all([RO_Request, servicesRequest]).then((res) => {
            this.getTaxSettings(res[0].data.tenantId);
            this.setState({
                repairOrder: res[0].data,
                isLoading: false,
                ...this.buildServicesState(res[1].data),
            });
        });
    }

    // Services the customer has already responded to keep whatever the shop has
    // on record; only the ones still awaiting a decision start selected.
    buildServicesState(services: Service[]) {
        const withSortedItems = services.map(sortItemsByCreatedAt);
        return {
            services: withSortedItems,
            approvedServiceIds: withSortedItems.filter((service) => !isDecided(service)).map((service) => service.id),
        };
    }

    getTaxSettings(tenantId: string) {
        axios.get(`${config.apiUrl}/tenant/public/taxSettings/${tenantId}`).then((res) => {
            this.setState({ tenantSettings: res.data });
        });
    }

    getPendingServices() {
        return this.state.services.filter((service) => !isDecided(service));
    }

    getSelectedServices() {
        return this.getPendingServices().filter((service) => this.state.approvedServiceIds.includes(service.id));
    }

    getUnselectedPendingServices() {
        return this.getPendingServices().filter((service) => !this.state.approvedServiceIds.includes(service.id));
    }

    getTotal(services: Service[]) {
        return services.reduce((acc, service) => acc + service.grandTotalCents, 0);
    }

    authorize() {
        if (this.getPendingServices().length === 0) return;
        this.setState({ isAuthorizationModalOpen: true });
    }

    // Re-reads the services so the page shows the decisions the server actually
    // recorded, which also locks the newly decided services against a second
    // submission from this same link.
    onAuthorized() {
        this.setState({ isAuthorizationModalOpen: false, hasJustAuthorized: true, isLoading: true });
        axios
            .get(`${config.apiUrl}/repairOrder/public/${this.state.repairOrderId}/services`)
            .then((res) => this.setState({ isLoading: false, ...this.buildServicesState(res.data) }))
            .catch(() => this.setState({ isLoading: false }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    renderServiceStatus(service: Service) {
        const { approvedServiceIds } = this.state;

        if (service.authorizationStatus === ServiceAuthorizationStatus.Authorized) {
            return <div className="font-semibold text-lg whitespace-nowrap text-green-600">Authorized</div>;
        }
        if (service.authorizationStatus === ServiceAuthorizationStatus.Declined) {
            return <div className="font-semibold text-lg whitespace-nowrap text-gray-500">Declined</div>;
        }

        const isSelected = approvedServiceIds.includes(service.id);
        return (
            <div className="flex">
                <div className="font-semibold text-lg mr-2 inline whitespace-nowrap">
                    {isSelected ? 'Authorize' : 'Decline'}
                </div>
                <Toggle
                    value={isSelected}
                    name="isAuthorized"
                    onChange={(newValue) =>
                        this.setState({
                            approvedServiceIds: newValue
                                ? [...approvedServiceIds, service.id]
                                : approvedServiceIds.filter((id) => id !== service.id),
                        })
                    }
                />
            </div>
        );
    }

    renderConfirmation() {
        const { hasJustAuthorized } = this.state;
        const authorizedServices = this.state.services.filter(
            (service) => service.authorizationStatus === ServiceAuthorizationStatus.Authorized,
        );

        return (
            <div className="w-11/12 sm:w-4/5 md:w-3/5 lg:w-2/5 mb-5 rounded-lg border-2 border-green-500 bg-green-50 p-4">
                <div className="text-xl font-semibold text-green-700">
                    <FaCheckCircle className="inline mr-2 align-baseline" />
                    {hasJustAuthorized ? 'Thank you - your response has been sent to the shop.' : 'Estimate answered'}
                </div>
                <div className="mt-1 text-gray-700">
                    {authorizedServices.length > 0 ? (
                        <>
                            You authorized {authorizedServices.length} service
                            {authorizedServices.length === 1 ? '' : 's'} totalling{' '}
                            <span className="font-semibold">
                                <Price price={this.getTotal(authorizedServices)} />
                            </span>
                            .
                        </>
                    ) : (
                        <>You declined all of the work on this estimate.</>
                    )}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                    Your answer is already on record - reopening this link will not send it again. Call the shop if you
                    need to change anything.
                </div>
            </div>
        );
    }

    renderAuthorizeAction() {
        const pendingServices = this.getPendingServices();
        const selectedServices = this.getSelectedServices();

        // Everything on the estimate has an answer - there is nothing left to
        // submit, so don't offer a button that would only be rejected.
        if (pendingServices.length === 0) {
            return null;
        }

        return (
            <div className="pt-5 w-11/12 pb-5 sm:w-4/5 md:w-3/5 lg:w-2/5">
                <div
                    onClick={() => this.authorize()}
                    className="flex flex-col text-center clickable bg-blue-700 text-2xl text-white font-semibold rounded-lg border-2 border-white py-4 w-full"
                >
                    <span>
                        {selectedServices.length > 0
                            ? `Authorize (${selectedServices.length} service${
                                  selectedServices.length === 1 ? '' : 's'
                              })`
                            : 'Decline all work'}
                    </span>
                    <span>
                        <Price price={this.getTotal(selectedServices)} />
                    </span>
                </div>
            </div>
        );
    }

    render() {
        const { repairOrder, services, isAuthorizationModalOpen, hasJustAuthorized } = this.state;
        const pendingServices = this.getPendingServices();
        const decidedServices = services.filter(isDecided);

        return (
            <div>
                {isAuthorizationModalOpen && (
                    <SignatureAuthorizationModal
                        declinedServices={this.getUnselectedPendingServices()}
                        repairOrderId={repairOrder!.id}
                        approvedServices={this.getSelectedServices()}
                        onClose={() => this.setState({ isAuthorizationModalOpen: false })}
                        onAuthorized={this.onAuthorized}
                    />
                )}
                {repairOrder && (
                    <div className="flex flex-col items-center">
                        <div className="mb-7 border-b-2 bg-gradient-to-r from-blue-700 to-blue-500 px-10 py-3 w-full">
                            <h2 className="text-lg font-semibold text-gray-100">{repairOrder.tenant?.businessName}</h2>

                            {repairOrder.tenant?.tenantSettings?.phoneNumber && (
                                <h2 className=" font-semibold text-gray-100">
                                    <FaPhoneSquare className="inline align-baseline mr-2" />
                                    <a className="mr-2" href={`tel:${repairOrder.tenant?.tenantSettings?.phoneNumber}`}>
                                        {repairOrder.tenant?.tenantSettings?.phoneNumber}
                                    </a>
                                </h2>
                            )}
                        </div>

                        {(hasJustAuthorized || (decidedServices.length > 0 && pendingServices.length === 0)) &&
                            this.renderConfirmation()}

                        <div className="pb-5 border-b-4 w-11/12 sm:w-4/5 md:w-3/5 lg:w-2/5">
                            <div className="mb-7 border-b-2">
                                <h2 className="text-2xl font-semibold">Estimate #{repairOrder?.repairOrderNumber}</h2>
                            </div>

                            <div className="mb-5">
                                <div>
                                    <FaCar className="inline mr-2 align-baseline text-gray-500" />
                                    <span>
                                        {repairOrder.vehicle?.year} {repairOrder.vehicle?.make}{' '}
                                        {repairOrder.vehicle?.model}
                                    </span>
                                </div>
                                <div>
                                    <FaUser className="inline mr-2 align-baseline text-gray-500" />
                                    <span>
                                        {repairOrder.customer?.firstName} {repairOrder.customer?.lastName}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex">
                                    <div className="w-1/3 sm:w-2/3 md:w-2/3 font-semibold border-b">
                                        Customer Concern
                                    </div>
                                    <div className="w-1/3 sm:w-2/3 md:w-2/3 font-semibold border-b">Tech Comment</div>
                                </div>

                                {repairOrder.vehicleIssues.map((problem) => (
                                    <div key={'concern' + problem.id} className="flex border-t py-1.5">
                                        <div
                                            key={'csoncern' + problem.id}
                                            className="leading-5 w-1/3 sm:w-2/3 md:w-2/3"
                                        >
                                            {problem.description}
                                        </div>
                                        <div key={'finding' + problem.id} className="leading-5 w-1/3 sm:w-2/3 md:w-2/3">
                                            {problem.finding}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pt-5 w-11/12 pb-5 sm:w-4/5 md:w-3/5 lg:w-2/5">
                            <div className="mb-7">
                                <h2 className="text-2xl font-semibold">Services</h2>
                                {pendingServices.length > 0 && decidedServices.length > 0 && (
                                    <p className="text-gray-600">
                                        {pendingServices.length} new service
                                        {pendingServices.length === 1 ? '' : 's'} need your approval. The rest are
                                        already answered and locked.
                                    </p>
                                )}
                            </div>

                            {services.map((service) => (
                                <div
                                    key={'service' + service.id}
                                    className={`border-2 p-2 py-4 mb-4 rounded-lg ${
                                        isDecided(service)
                                            ? 'bg-gray-100 border-gray-300'
                                            : 'bg-white border-blue-300'
                                    }`}
                                >
                                    <div className="flex text-xl">
                                        <h2 className="font-mono font-semibold">{service.title}</h2>
                                        <div className="ml-auto flex">{this.renderServiceStatus(service)}</div>
                                    </div>
                                    <div className="flex my-2 border-b-2 border-gray-500"></div>

                                    {service.items?.map((item) => (
                                        <div key={'serviceItem' + item.id} className="flex">
                                            <div className="font-semibold">
                                                {item.inventoryItemId
                                                    ? item.inventoryItem?.description
                                                    : item.description}
                                            </div>
                                            <div className="ml-auto">
                                                <Price price={item.totalCents} />
                                            </div>
                                        </div>
                                    ))}

                                    <div className="border-t-2 border-gray-500 flex mt-2 pt-1.5">
                                        <div className="font-semibold">
                                            Subtotal:{' '}
                                            <span>
                                                <Price price={service.totalCents} />
                                            </span>
                                        </div>

                                        <div className="">
                                            <span className="ml-6 font-semibold">
                                                Tax: <Price price={service.totalTaxCents} />
                                            </span>
                                        </div>

                                        <div className="ml-auto ">
                                            <span className="ml-2 font-semibold text-lg">
                                                Service Total: <Price price={service.grandTotalCents} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="pt-5 w-11/12 pb-5 sm:w-4/5 md:w-3/5 lg:w-2/5">
                            <div className="w-full float-left">
                                <div className="font-semibold">Grand Total:</div>
                                <div className="flex">
                                    <div>Estimated</div>
                                    <div className="ml-auto font-semibold">
                                        <Price price={this.getTotal(services)} />
                                    </div>
                                </div>
                                {decidedServices.length > 0 && (
                                    <div className="flex">
                                        <div>Already authorized</div>
                                        <div className="ml-auto font-semibold">
                                            <Price
                                                price={this.getTotal(
                                                    services.filter(
                                                        (service) =>
                                                            service.authorizationStatus ===
                                                            ServiceAuthorizationStatus.Authorized,
                                                    ),
                                                )}
                                            />
                                        </div>
                                    </div>
                                )}
                                {pendingServices.length > 0 && (
                                    <div className="flex">
                                        <div>Selected for approval</div>
                                        <div className="ml-auto font-semibold">
                                            <Price price={this.getTotal(this.getSelectedServices())} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {this.renderAuthorizeAction()}
                    </div>
                )}
            </div>
        );
    }
}

export default EstimatePage;
